import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Badge, Button, createStyles, Group, Menu, SegmentedControl, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import _ from 'underscore';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import HomeDashboardStore from 'home-dashboard/HomeDashboardStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Spinner from 'ui-library/Spinner';
import strUtil from 'util/strUtil';

import {HomepageAssignedProcessTasksQuery} from './__generated/HomepageAssignedProcessTasksQuery';
import CardSection from './CardSection';
import {HOMEPAGE_SECTION_TEST_COMPONENT} from './HomepageConstants';
import {
  getEnabledMyWorkCategories,
  hasReviewsLicense,
  shouldShowAssignedProcessTasks,
} from './HomepageVisibility';
import EntriesRows from './my-work/EntriesRows';
import {
  MyWorkCategory,
  REVIEW_ITEM_STATUS_LABELS,
  REVIEW_STATUS_LABEL_TO_SUMMARY_KEY,
  TASK_STATUS_LABELS,
  TASK_STATUS_LABEL_TO_SUMMARY_KEY,
} from './my-work/MyWorkConstants';
import {
  getSelectAllMenuIcon,
  getStatusMenuButtonLabel,
  getStatusSelectionState,
  toggleAllStatusSelections,
  toggleStatusSelection,
} from './my-work/MyWorkStatusFilter';
import {MyWorkStatusMenuItem} from './my-work/MyWorkStatusMenuItem';
import {
  getCategoryCounts,
  getSelectedReviewSummaryIds,
  getSelectedTaskIds,
  makeTasksReviewSummary,
} from './my-work/MyWorkUtils';
import {openMyWorkReviewableSearch, openAllAssignedTasksSearch} from './my-work/openMyWorkSearch';
import TasksRows from './my-work/TasksRows';
import WorksheetsRows from './my-work/WorksheetsRows';

export const HOMEPAGE_ASSIGNED_PROCESS_TASKS_QUERY = gql`
  query HomepageAssignedProcessTasksQuery @skipAudit @engComponent(name: NAVIGATION) {
    assignedProcessTaskStatusSummaries(
      processTaskStatusTypes: [PENDING, IN_PROGRESS, COMPLETED, FAILED, INVALID, SKIPPED, CANCELLED]
    ) {
      ... on ProcessTaskStatusSummaryItemNode {
        count
        taskIds
        status {
          ... on ProcessTaskStatusNode {
            id
            displayName
            statusType
          }
        }
      }
    }
  }
`;

const TEST_COMPONENT = HOMEPAGE_SECTION_TEST_COMPONENT.MY_WORK;

const useMyWorkMenuIconStyles = createStyles((theme, {active}: {active: boolean}) => ({
  icon: {
    '&&': {
      width: '16px',
      height: '16px',
    },
    color: active ? theme.colors.blue[6] : theme.colors.gray[3],
  },
}));

const useMyWorkStyles = createStyles((theme) => ({
  filterRow: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    padding: theme.spacing.md,
  },
  flexGrow: {
    '&&': {
      flexGrow: 1,
    },
  },
}));

const useCategoryLabelStyles = createStyles((theme, {selected}: {selected: boolean}) => ({
  spinner: {
    '&&': {
      width: '8px',
      height: '8px',
    },
  },
  badge: {
    fontSize: '11px',
    backgroundColor: selected ? theme.white : theme.colors.gray[1],
  },
}));

function CategoryLabel({
  category,
  count,
  selected,
}: {
  category: MyWorkCategory;
  count: number | 'Loading';
  selected: boolean;
}): JSX.Element {
  const {classes} = useCategoryLabelStyles({selected});
  const badge =
    count === 'Loading' ? (
      <Spinner classNames={classes.spinner} light={true} />
    ) : (
      <Badge size="lg" radius="lg" color={selected ? 'white' : 'gray'} className={classes.badge}>
        {count}
      </Badge>
    );

  return (
    <Group noWrap={true} spacing="xs">
      {strUtil.toSentenceCase(category.toString())}
      {badge}
    </Group>
  );
}

/** Entry, worksheet, and process-task work queues with per-category status filters. */
export default function MyWorkSection(): JSX.Element | null {
  const showAssignedProcessTasks = shouldShowAssignedProcessTasks();

  useOnMount(() => {
    // Browsers cap the number of simultaneous network requests to a given domain - typically around 6.
    // <Sidepanel> (which renders this component) does a few expensive GraphQL queries, and since Apollo
    // always defers queries for a tick, if we don't match that behavior here, these network requests are
    // fired first, and ultimately make page load slower (we want the slow network requests to start as soon
    // as possible).
    _.defer(() => {
      if (hasReviewsLicense()) {
        HomeDashboardActions.fetchEntryReviewSummary();
        HomeDashboardActions.fetchWorksheetReviewSummary();
      }
    });
  });

  const entryReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getEntryReviewSummary(), [])
  );
  const worksheetReviewSummary = useStoreSelector(
    HomeDashboardStore,
    useCallback((store) => store.getWorksheetReviewSummary(), [])
  );

  const enabledCategories = useMemo(() => getEnabledMyWorkCategories(), []);

  const [selectedCategory, setSelectedCategory] = useState<MyWorkCategory>(MyWorkCategory.ENTRIES);
  const [selectedEntryStatuses, setSelectedEntryStatuses] = useState(REVIEW_ITEM_STATUS_LABELS);
  const [selectedWorksheetStatuses, setSelectedWorksheetStatuses] = useState(REVIEW_ITEM_STATUS_LABELS);
  const [selectedTaskStatuses, setSelectedTaskStatuses] = useState(TASK_STATUS_LABELS);
  const {classes} = useMyWorkStyles();

  useEffect(() => {
    if (enabledCategories.length > 0 && !enabledCategories.includes(selectedCategory)) {
      setSelectedCategory(enabledCategories[0]);
    }
  }, [enabledCategories, selectedCategory]);

  const tasksQueryResult = useSkippableBenchlingQuery<HomepageAssignedProcessTasksQuery>(
    HOMEPAGE_ASSIGNED_PROCESS_TASKS_QUERY,
    {skip: !showAssignedProcessTasks}
  );
  const tasksReviewSummary = useMemo(() => makeTasksReviewSummary(tasksQueryResult), [tasksQueryResult]);

  const statusSelection = getStatusSelectionState(
    selectedCategory === MyWorkCategory.TASKS
      ? selectedTaskStatuses
      : selectedCategory === MyWorkCategory.WORKSHEETS
        ? selectedWorksheetStatuses
        : selectedEntryStatuses,
    selectedCategory === MyWorkCategory.TASKS ? TASK_STATUS_LABELS : REVIEW_ITEM_STATUS_LABELS
  );
  const {classes: menuIconClasses} = useMyWorkMenuIconStyles({active: statusSelection !== 'none'});

  if (enabledCategories.length === 0) {
    return null;
  }

  const categoryUi = {
    [MyWorkCategory.ENTRIES]: {
      statusLabels: REVIEW_ITEM_STATUS_LABELS,
      selectedStatuses: selectedEntryStatuses,
      setSelectedStatuses: setSelectedEntryStatuses,
    },
    [MyWorkCategory.WORKSHEETS]: {
      statusLabels: REVIEW_ITEM_STATUS_LABELS,
      selectedStatuses: selectedWorksheetStatuses,
      setSelectedStatuses: setSelectedWorksheetStatuses,
    },
    [MyWorkCategory.TASKS]: {
      statusLabels: TASK_STATUS_LABELS,
      selectedStatuses: selectedTaskStatuses,
      setSelectedStatuses: setSelectedTaskStatuses,
    },
  }[selectedCategory];

  const {entriesCount, worksheetsCount, tasksCount} = getCategoryCounts({
    entryReviewSummary,
    worksheetReviewSummary,
    tasksQueryResult,
    showEntriesCategory: enabledCategories.includes(MyWorkCategory.ENTRIES),
    showWorksheetsCategory: enabledCategories.includes(MyWorkCategory.WORKSHEETS),
    showTasksCategory: enabledCategories.includes(MyWorkCategory.TASKS),
  });

  const toDoCategoryLabels: Record<MyWorkCategory, React.ReactNode> = {
    [MyWorkCategory.ENTRIES]: (
      <CategoryLabel
        category={MyWorkCategory.ENTRIES}
        count={entriesCount}
        selected={selectedCategory === MyWorkCategory.ENTRIES}
      />
    ),
    [MyWorkCategory.WORKSHEETS]: (
      <CategoryLabel
        category={MyWorkCategory.WORKSHEETS}
        count={worksheetsCount}
        selected={selectedCategory === MyWorkCategory.WORKSHEETS}
      />
    ),
    [MyWorkCategory.TASKS]: (
      <CategoryLabel
        category={MyWorkCategory.TASKS}
        count={tasksCount}
        selected={selectedCategory === MyWorkCategory.TASKS}
      />
    ),
  };

  const statusMenuLabel = getStatusMenuButtonLabel(categoryUi.selectedStatuses, statusSelection);
  const isTaskCategory = selectedCategory === MyWorkCategory.TASKS;
  const reviewStatusCounts =
    selectedCategory === MyWorkCategory.ENTRIES ? entryReviewSummary : worksheetReviewSummary;

  return (
    <CardSection title="My work" dataTestAttrs={{component: TEST_COMPONENT, element: 'root'}}>
      <Group spacing="xs" className={classes.filterRow}>
        {/* If there's only one category, don't show the segmented control */}
        {enabledCategories.length === 1 ? (
          <Text>{toDoCategoryLabels[enabledCategories[0]]}</Text>
        ) : (
          <SegmentedControl
            aria-label="Select category"
            variant="contained"
            data={enabledCategories.map((category) => ({
              value: category,
              label: toDoCategoryLabels[category],
            }))}
            value={selectedCategory.toString()}
            onChange={(value) => {
              setSelectedCategory(value as MyWorkCategory);
            }}
            {...getTestProps({component: TEST_COMPONENT, element: 'categoryControl'})}
          />
        )}
        <Group position="apart" noWrap={true} className={classes.flexGrow}>
          <Menu position="bottom-start" width="300px" withinPortal={true} closeOnItemClick={false}>
            <Menu.Target>
              <Button
                variant="ghost"
                size="md"
                rightIcon={<BenchlingIcon icon="chevron-down" />}
                {...getTestProps({component: TEST_COMPONENT, element: 'statusMenuButton'})}
              >
                {statusMenuLabel}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                key="all"
                icon={
                  <BenchlingIcon
                    icon={getSelectAllMenuIcon(statusSelection)}
                    className={menuIconClasses.icon}
                    aria-hidden={true}
                  />
                }
                {...getTestProps({component: TEST_COMPONENT, element: 'statusMenuItem', key: 'toggleAll'})}
                onClick={() => {
                  categoryUi.setSelectedStatuses(
                    toggleAllStatusSelections(categoryUi.selectedStatuses, categoryUi.statusLabels)
                  );
                }}
              >
                Select all
              </Menu.Item>
              <Menu.Divider />
              {categoryUi.statusLabels.map((statusLabel) => {
                const sharedProps = {
                  key: statusLabel,
                  isSelected: categoryUi.selectedStatuses.includes(statusLabel),
                  setIsSelected: (selected: boolean) => {
                    categoryUi.setSelectedStatuses(
                      toggleStatusSelection(categoryUi.selectedStatuses, statusLabel, selected)
                    );
                  },
                  statusLabel,
                  dataTestAttrs: {
                    component: TEST_COMPONENT,
                    element: 'statusMenuItem' as const,
                    key: statusLabel,
                  },
                };
                const statusMenuItemProps = isTaskCategory
                  ? {
                      isTask: true as const,
                      statusCounts: tasksReviewSummary,
                      summaryKey: TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel],
                    }
                  : {
                      isTask: false as const,
                      statusCounts: reviewStatusCounts,
                      summaryKey: REVIEW_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel],
                    };
                return <MyWorkStatusMenuItem {...sharedProps} {...statusMenuItemProps} />;
              })}
            </Menu.Dropdown>
          </Menu>
          <Button
            size="sm"
            // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design (yet)
            variant="default"
            color="gray"
            onClick={() => {
              if (selectedCategory === MyWorkCategory.ENTRIES) {
                openMyWorkReviewableSearch([ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY]);
              } else if (selectedCategory === MyWorkCategory.WORKSHEETS) {
                openMyWorkReviewableSearch([ModelTypeConstants.WORKSHEET]);
              } else if (selectedCategory === MyWorkCategory.TASKS) {
                openAllAssignedTasksSearch();
              }
            }}
          >
            See all {selectedCategory}
          </Button>
        </Group>
      </Group>
      <div>
        {selectedCategory === MyWorkCategory.ENTRIES && (
          <EntriesRows
            selectedEntryIds={getSelectedReviewSummaryIds(entryReviewSummary, selectedEntryStatuses)}
          />
        )}
        {selectedCategory === MyWorkCategory.WORKSHEETS && (
          <WorksheetsRows
            selectedWorksheetIds={getSelectedReviewSummaryIds(
              worksheetReviewSummary,
              selectedWorksheetStatuses
            )}
          />
        )}
        {selectedCategory === MyWorkCategory.TASKS && (
          <TasksRows selectedTaskIds={getSelectedTaskIds(tasksReviewSummary, selectedTaskStatuses)} />
        )}
      </div>
    </CardSection>
  );
}
